import { useEffect, useMemo, useState } from "react";
import {
  EmploymentType,
  Instructor,
  InstructorListSortDir,
  InstructorListSortField,
  buildWhatsAppUrl,
  certificateStatus,
  useInstructorList,
} from "../api/instructors";
import { SortableTableHeader } from "../components/common/SortableTableHeader";
import {
  ActivePassiveFilterSelect,
  DEFAULT_ACTIVE_PASSIVE_FILTER,
  activePassiveToTriState,
  type ActivePassiveFilter,
} from "../components/common/ActivePassiveFilterSelect";
import { BranchFilterSelect } from "../components/common/BranchFilterSelect";
import { INSTRUCTORS_IMPORT } from "../api/imports";
import { usePlatformBranchFilter } from "../hooks/usePlatformBranchFilter";
import { useAuth } from "../auth/AuthContext";
import ExcelImportModal from "../components/imports/ExcelImportModal";
import { WhatsAppLink } from "../components/WhatsAppLink";
import { formatPhoneDisplay } from "../utils/phone";
import { InstructorFormModal } from "../components/instructors/InstructorFormModal";
import { InstructorLeaveModal } from "../components/instructors/InstructorLeaveModal";
import { UI_LOADING } from "../constants/uiLabels";
import { ListPaginationBar, useListPageSize, usePagedOffset } from "../components/ListPagination";
import { useRegisterListPageStats } from "../context/ListPageStatsContext";
import "./InstructorsPage.css";

const EMPLOYMENT_LABEL: Record<EmploymentType, string> = {
  full_time: "Tam zamanli",
  daily_paid: "Gunluk ucretli",
};

function formatDate(value: string | null): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("tr-TR", { dateStyle: "medium" });
}

export function InstructorsPage() {
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState<ActivePassiveFilter>(
    DEFAULT_ACTIVE_PASSIVE_FILTER,
  );
  const [employmentFilter, setEmploymentFilter] = useState<EmploymentType | "">("");
  const [editing, setEditing] = useState<Instructor | null>(null);
  const [creating, setCreating] = useState(false);
  const [leaveFor, setLeaveFor] = useState<Instructor | null>(null);
  const [importing, setImporting] = useState(false);
  const [sortBy, setSortBy] = useState<InstructorListSortField>("full_name");
  const [sortDir, setSortDir] = useState<InstructorListSortDir>("asc");

  const { session } = useAuth();
  const role = session?.user?.role;
  const canImport = role === "tenant_admin" || role === "manager" || role === "office_staff";
  const { isPlatformOwner, branchFilter, setBranchFilter, branches } = usePlatformBranchFilter();

  useEffect(() => {
    const handle = setTimeout(() => {
      setAppliedSearch(searchInput.trim());
    }, 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const handleSort = (column: InstructorListSortField) => {
    if (sortBy === column) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(column);
    setSortDir("asc");
  };

  const pageSize = useListPageSize();
  const { offset, setOffset } = usePagedOffset(
    JSON.stringify([appliedSearch, activeFilter, employmentFilter, branchFilter, sortBy, sortDir, pageSize]),
  );

  const list = useInstructorList({
    q: appliedSearch || undefined,
    isActive: activePassiveToTriState(activeFilter),
    employmentType: employmentFilter || undefined,
    branchId: branchFilter || undefined,
    sort: sortBy,
    order: sortDir,
    limit: pageSize,
    offset,
  });

  const items = list.data?.items || [];
  const total = list.data?.total || 0;

  useRegisterListPageStats({
    label: "eğitmen",
    visible: items.length,
    total,
    loading: list.loading,
  });

  const summary = useMemo(() => `${total} kayit`, [total]);

  return (
    <div className="kp-instructors">
      <div className="kp-instructors__header">
        <div>
          <h1>Eğitmenler</h1>
          <p>{summary}</p>
        </div>
        <div className="kp-instructors__header-actions">
          {canImport && (
            <button className="kp-btn" onClick={() => setImporting(true)}>
              Excel İle Toplu Yükle
            </button>
          )}
          <button className="kp-btn kp-btn--primary" onClick={() => setCreating(true)}>
            + Yeni Eğitmen
          </button>
        </div>
      </div>

      <div className="kp-instructors__toolbar kp-card">
        <input
          className="kp-input"
          type="search"
          placeholder="Ad, telefon veya e-posta ile ara..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <ActivePassiveFilterSelect value={activeFilter} onChange={setActiveFilter} />
        <select
          className="kp-input"
          value={employmentFilter}
          onChange={(e) => setEmploymentFilter(e.target.value as EmploymentType | "")}
        >
          <option value="">Tum calisma turleri</option>
          <option value="full_time">Tam zamanli</option>
          <option value="daily_paid">Gunluk ucretli</option>
        </select>
        {isPlatformOwner && (
          <BranchFilterSelect
            value={branchFilter}
            onChange={setBranchFilter}
            branches={branches}
          />
        )}
        <button className="kp-btn" onClick={list.reload} disabled={list.loading}>
          Yenile
        </button>
      </div>

      <div className="kp-instructors__table-wrapper kp-card">
        {list.loading && <div className="kp-loading">{UI_LOADING}</div>}
        {list.error && <div className="kp-error">{list.error}</div>}

        {!list.loading && !list.error && items.length === 0 && (
          <div className="kp-empty">Bu kriterlere uyan egitmen bulunamadi.</div>
        )}

        {!list.loading && !list.error && items.length > 0 && (
          <table className="kp-instructors__table kp-data-table">
            <thead>
              <tr>
                <SortableTableHeader
                  label="Ad Soyad"
                  column="full_name"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortableTableHeader
                  label="Iletisim"
                  column="phone"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortableTableHeader
                  label="Calisma Turu"
                  column="employment_type"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortableTableHeader
                  label="Sertifika"
                  column="certificate_expiry_date"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortableTableHeader
                  label="Durum"
                  column="is_active"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <th scope="col" aria-label="Aksiyonlar"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((instructor) => {
                const wa = buildWhatsAppUrl(instructor.phone);
                const cert = certificateStatus(instructor.certificate_expiry_date);
                return (
                  <tr key={instructor.id}>
                    <td>
                      <strong>{instructor.full_name}</strong>
                      {instructor.user_id && (
                        <small className="kp-instructors__sub">Kullanıcı eslesmis</small>
                      )}
                    </td>
                    <td className="kp-cell-phone">
                      <div className="kp-instructors__contact">
                        {instructor.phone ? formatPhoneDisplay(instructor.phone) : "-"}
                        {wa && <WhatsAppLink href={wa} />}
                      </div>
                      {instructor.email && (
                        <small className="kp-instructors__sub">{instructor.email}</small>
                      )}
                    </td>
                    <td>
                      <span
                        className={`kp-instructors__employment kp-instructors__employment--${instructor.employment_type}`}
                      >
                        {EMPLOYMENT_LABEL[instructor.employment_type]}
                      </span>
                    </td>
                    <td>
                      <div className={`kp-instructors__cert kp-instructors__cert--${cert.tone}`}>
                        <span>{formatDate(instructor.certificate_expiry_date)}</span>
                        <small>{cert.label}</small>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`kp-instructors__status kp-instructors__status--${
                          instructor.is_active ? "active" : "passive"
                        }`}
                      >
                        {instructor.is_active ? "Aktif" : "Pasif"}
                      </span>
                    </td>
                    <td>
                      <div className="kp-instructors__row-actions">
                        <button
                          type="button"
                          className="kp-btn kp-instructors__action"
                          onClick={() => setEditing(instructor)}
                        >
                          Düzenle
                        </button>
                        <button
                          type="button"
                          className="kp-btn kp-instructors__action kp-instructors__action--leave"
                          onClick={() => setLeaveFor(instructor)}
                        >
                          Izin Ekle
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <ListPaginationBar
          total={total}
          offset={offset}
          limit={pageSize}
          loading={list.loading}
          onOffsetChange={setOffset}
        />
      </div>

      {(creating || editing) && (
        <InstructorFormModal
          instructor={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            list.reload();
          }}
        />
      )}

      {leaveFor && (
        <InstructorLeaveModal
          instructor={leaveFor}
          onClose={() => setLeaveFor(null)}
          onSaved={() => {
            setLeaveFor(null);
            list.reload();
          }}
        />
      )}

      {importing && (
        <ExcelImportModal
          title="Eğitmen Toplu Yükleme (Excel)"
          description="Şablonu indirip dolduktan sonra Yükleyin. Zorunlu kolonlar: Ad Soyad, Çalışma Tipi."
          endpoint={INSTRUCTORS_IMPORT}
          successLabel={(n) => `${n} egitmen Başarıyla eklendi.`}
          onClose={() => setImporting(false)}
          onImported={() => {
            list.reload();
          }}
        />
      )}
    </div>
  );
}
